import { createSelector } from 'reselect';
import { findIndex, some } from 'lodash';
import { appSelectors } from 'store/app';
import { pagesSelectors } from 'store/pages';
import PAGES from 'constants/pages';
import PLAYER_TYPES from 'constants/player-types';
import { RootStateType } from 'store/rootReducer';
import { QueryParamsType } from './types';

type CreateSelectorParamsType = {
  playerType: string;
  queryParams: QueryParamsType;
};

export const selectPlayerType = createSelector(appSelectors.selectLocationPath, (path) => {
  if (/movie/.test(path)) {
    return PLAYER_TYPES.MOVIE;
  } else if (/series/.test(path)) {
    return PLAYER_TYPES.SERIES;
  } else if (/channel/.test(path)) {
    return PLAYER_TYPES.CHANNEL;
  } else if (/trailer/.test(path)) {
    return PLAYER_TYPES.TRAILER;
  }
});

export const createPlayerContentSelector = ({
  playerType,
  queryParams,
}: CreateSelectorParamsType) => {
  let pageId: string = PAGES.DETAILS;
  let params = {
    id: queryParams.objectId,
    type: playerType === PLAYER_TYPES.MOVIE ? 'movie' : 'series',
  };

  if (playerType === PLAYER_TYPES.CHANNEL) {
    pageId = PAGES.LIVE_TV;
    params = {
      id: queryParams.channelId,
      type: '',
    };
  }
  return createSelector(
    (state: RootStateType) => pagesSelectors.selectPageData(state, { pageId, params }),
    (pageData) => {
      if (playerType === PLAYER_TYPES.CHANNEL) return pageData;
      return (pageData || {}).details;
    },
  );
};

export const createPlayerContentIsLoadingSelector = ({
  playerType,
  queryParams,
}: CreateSelectorParamsType) => {
  let pageId: string = PAGES.DETAILS;
  let params = {
    id: queryParams.objectId,
    type: playerType === PLAYER_TYPES.MOVIE ? 'movie' : 'series',
  };

  if (playerType === PLAYER_TYPES.CHANNEL) {
    pageId = PAGES.LIVE_TV;
    params = {
      id: queryParams.channelId,
      type: '',
    };
  }
  return createSelector(
    (state: RootStateType) => pagesSelectors.selectPageIsLoading(state, { pageId, params }),
    (isLoading) => {
      return isLoading;
    },
  );
};

export const createPlayerNavigationSelector = ({
  playerType,
  queryParams,
}: CreateSelectorParamsType) => {
  let pageId: string = PAGES.DETAILS;
  let params = {
    id: queryParams.objectId,
    type: playerType === PLAYER_TYPES.MOVIE ? 'movie' : 'series',
  };

  if (playerType === PLAYER_TYPES.CHANNEL) {
    pageId = PAGES.LIVE_TV;
    params = {
      id: queryParams.channelId,
      type: '',
    };
  }

  return createSelector(
    (state: RootStateType) => {
      if (playerType === PLAYER_TYPES.CHANNEL) params = null;
      return pagesSelectors.selectPageData(state, { pageId, params });
    },
    (pageData) => {
      const navigation = {
        prev: null,
        next: null,
      };

      if (playerType === PLAYER_TYPES.SERIES) {
        const currentSeason = pageData?.details?.series_seasons.find(({ episodes }) =>
          some(episodes, { id: queryParams.episodeId }),
        );
        const { episodes = [] } = currentSeason || {};
        const currentEpisodeIndex = findIndex(episodes, { id: queryParams.episodeId });
        const prevEpisode = episodes[currentEpisodeIndex - 1];
        const nextEpisode = episodes[currentEpisodeIndex + 1];

        if (prevEpisode) navigation.prev = { id: prevEpisode.id };
        if (nextEpisode) navigation.next = { id: nextEpisode.id };
      }

      if (playerType === PLAYER_TYPES.CHANNEL) {
        const content = pageData?.content || [];
        const currentChannelIndex = content.findIndex(({ id }) => id === queryParams.channelId);

        const prevChannel = content[currentChannelIndex - 1];
        const nextChannel = content[currentChannelIndex + 1];

        if (prevChannel) navigation.prev = { id: prevChannel.id };
        if (nextChannel) navigation.next = { id: nextChannel.id };
      }
      return navigation;
    },
  );
};
